import { useCallback, useEffect, useState } from 'react';
import { colorIndexFor, generateUserName, type AwarenessUser } from './types';

const STORAGE_KEY = 'frame-user';
const STORAGE_EVENT = 'frame-user:updated';

interface StoredUser {
  id: string;
  name: string;
  // Optional override; when absent, color is derived from id (the original
  // behavior). Settings popover sets this when the user picks a swatch.
  colorIndex?: number;
}

// Persisted per-browser identity. Day 22+ replaces with auth-driven user
// (the JWT subject claim flows in here so guests and the owner get stable
// names across sessions).
//
// Returns `[user, updateUser]`. Updates are persisted to localStorage and
// dispatched to all other useLocalUser consumers in the same tab via a
// custom window event so the Settings popover and the awareness emitter
// stay in sync without prop-drilling.
export function useLocalUser(): [AwarenessUser, (patch: Partial<Pick<AwarenessUser, 'name' | 'colorIndex'>>) => void] {
  const [user, setUser] = useState<AwarenessUser>(() => readOrCreate());

  useEffect(() => {
    const onUpdate = (e: Event) => {
      const next = (e as CustomEvent<AwarenessUser>).detail;
      setUser(next);
    };
    window.addEventListener(STORAGE_EVENT, onUpdate);
    return () => window.removeEventListener(STORAGE_EVENT, onUpdate);
  }, []);

  const updateUser = useCallback(
    (patch: Partial<Pick<AwarenessUser, 'name' | 'colorIndex'>>) => {
      const next: AwarenessUser = { ...user, ...patch };
      writeStored({ id: next.id, name: next.name, colorIndex: next.colorIndex });
      window.dispatchEvent(new CustomEvent(STORAGE_EVENT, { detail: next }));
    },
    [user],
  );

  return [user, updateUser];
}

function readOrCreate(): AwarenessUser {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<StoredUser>;
      if (parsed.id && parsed.name) {
        return {
          id: parsed.id,
          name: parsed.name,
          colorIndex:
            typeof parsed.colorIndex === 'number'
              ? parsed.colorIndex
              : colorIndexFor(parsed.id),
          isFacilitator: false,
        };
      }
    }
  } catch {
    /* ignore corrupt storage */
  }
  const fresh = freshUser();
  writeStored({ id: fresh.id, name: fresh.name });
  return fresh;
}

function writeStored(stored: StoredUser): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    /* quota / SSR */
  }
}

function freshUser(): AwarenessUser {
  const id = randomId();
  return {
    id,
    name: generateUserName(),
    colorIndex: colorIndexFor(id),
    isFacilitator: false,
  };
}

function randomId(): string {
  // 10 chars is plenty for "is this the same person across reloads". Avoid
  // crypto.randomUUID so this works in older Brave private-browsing tabs.
  const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let out = '';
  for (let i = 0; i < 10; i++) out += alphabet[Math.floor(Math.random() * alphabet.length)];
  return out;
}
